import { Building2 } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * `apps/web-admin/src/routes/customers/`: button labels and states quoted
 * verbatim from `customer-list-page.tsx`, `customer-data-tab.tsx`,
 * `customer-sites-tab.tsx` and `customer-contacts-tab.tsx`. `requiredPermission`
 * mirrors `customer.read` in `layout/navigation.ts`.
 */
export const CUSTOMERS_MODULE: GuideModule = {
  id: 'customers',
  title: 'Clientes',
  icon: Building2,
  requiredPermission: 'customer.read',
  priority: 3,
  summary:
    'Acá gestionás las empresas que contratan el servicio de catering: sus datos generales, sus sedes (los lugares físicos donde se entrega) y sus contactos (las personas con las que hablás). Una empresa puede tener varias sedes, y cada sede puede tener su propia ventana horaria de entrega. Si una empresa deja de operar temporalmente, la suspendés en vez de eliminarla; si el vínculo termina de forma definitiva, la das de baja. Todo lo que cargues acá lo va a usar el resto del sistema: contratos, calendario y, más adelante, pedidos.',
  steps: [
    {
      title: 'Dar de alta una empresa nueva',
      description:
        'Andá a Clientes y hacé clic en "Nueva empresa". Completá razón social, nombre comercial, CUIT, condición fiscal, email de facturación y zona, y guardá.',
      keyTerms: ['empresa', 'zona'],
    },
    {
      title: 'Agregar una sede a una empresa',
      description:
        'Entrá al detalle de la empresa, andá a la pestaña "Sedes" y hacé clic en "Nueva sede". Cargá el nombre, el domicilio y la ventana horaria de entrega si corresponde.',
      keyTerms: ['sede', 'ventana-de-entrega'],
    },
    {
      title: 'Cargar un contacto',
      description:
        'Desde la pestaña "Contactos", hacé clic en "Nuevo contacto". Elegí si es corporativo o de una sede en particular, y completá nombre, rol, email y teléfono.',
      keyTerms: ['contacto', 'corporativo'],
    },
    {
      title: 'Suspender una empresa',
      description:
        'En la pestaña "Datos" del detalle de la empresa, hacé clic en "Suspender" y escribí el motivo. Es obligatorio explicar por qué.',
      keyTerms: ['suspension'],
    },
    {
      title: 'Reactivar una empresa suspendida',
      description:
        'Con la empresa suspendida abierta, hacé clic en "Reactivar" y confirmá. La empresa vuelve a quedar activa.',
      keyTerms: ['reactivacion'],
    },
    {
      title: 'Marcar una sede como sede por defecto',
      description:
        'En la pestaña "Sedes", hacé clic en "Marcar como sede por defecto" en la sede que quieras. Tiene que estar activa primero.',
      keyTerms: ['sede-por-defecto'],
    },
    {
      title: 'Dar acceso al portal',
      description:
        'En la pestaña "Accesos al portal", hacé clic en "Invitar al portal" y elegí un contacto de la empresa que todavía no tenga un usuario del portal vinculado, un rol (Consulta de Cliente, Encargado de Compras o Supervisor de Cliente) y las sedes a las que va a poder acceder. El contacto recibe una invitación por email; el acceso queda "Invitación pendiente" hasta que la acepta.',
      keyTerms: ['acceso-al-portal'],
    },
    {
      title: 'Ver los contratos de una empresa',
      description:
        'En la pestaña "Contratos" del detalle de la empresa vas a ver todos sus contratos, con código, versión, estado y vigencia. Hacé clic en "Nuevo contrato" para crear uno nuevo con esa empresa ya seleccionada, o hacé clic en cualquier fila para entrar a su detalle.',
      keyTerms: ['contrato'],
    },
  ],
  states: [
    { value: 'ACTIVE', label: 'Activa', description: 'La empresa opera con normalidad.' },
    {
      value: 'SUSPENDED',
      label: 'Suspendida',
      description: 'La empresa está temporalmente fuera de operación, con un motivo cargado.',
    },
    {
      value: 'INACTIVE',
      label: 'Inactiva',
      description: 'La empresa fue dada de baja de forma definitiva.',
    },
  ],
  faq: [
    {
      question: '¿Cuál es la diferencia entre suspender y dar de baja una empresa?',
      answer:
        'Suspender es temporal y se puede revertir con "Reactivar". Dar de baja es definitivo: la empresa deja de aparecer en el sistema y solo se puede revertir desde soporte.',
    },
    {
      question: '¿Puedo eliminar una sede que es la sede por defecto?',
      answer:
        'No. Primero tenés que marcar otra sede como sede por defecto; recién ahí vas a poder desactivar o eliminar la anterior.',
    },
    {
      question: '¿Qué pasa si un contacto no está vinculado a ninguna sede?',
      answer:
        'Se lo considera "Corporativo": pertenece a la empresa en general, no a una sede en particular.',
    },
    {
      question: '¿Por qué no puedo marcar una sede inactiva como sede por defecto?',
      answer: 'Primero tenés que activarla con el botón "Activar" de esa sede.',
    },
    {
      question: '¿Por qué un contacto no aparece en el selector de "Invitar al portal"?',
      answer:
        'Porque ese contacto ya tiene un usuario del portal vinculado. Solo se ofrecen contactos que todavía no fueron invitados.',
    },
    {
      question: '¿Puedo cambiar el rol de un usuario del portal ya invitado?',
      answer:
        'No desde "Editar": el rol no se puede modificar una vez creado el acceso. Si necesitás otro rol, revocá el acceso actual e invitá de nuevo al contacto.',
    },
  ],
  tips: [
    'Cargá la zona correcta desde el alta: la usan los feriados de zona en el Calendario.',
    'Antes de dar de baja una empresa, revisá que no tenga contratos activos.',
    'Si vas a suspender una empresa, escribí un motivo claro: queda guardado y visible en el detalle.',
    'Mantené actualizado al menos un contacto que reciba notificaciones por sede.',
  ],
};
